/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useState, useCallback, MouseEvent, useEffect, useRef } from 'react';
import type { ReactElement, JSXElementConstructor } from 'react';
import { Site } from '../types/Site';

const FilterOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FilterPanel = styled.div`
  position: relative;
  z-index: 1000;
  background: #1a1a1a;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  width: 420px;
  max-width: 90vw;
  box-sizing: border-box;
  animation: fadeIn 0.2s ease-out forwards;
  max-height: 80vh;
  overflow-y: auto;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 640px) {
    width: 90vw;
    padding: 12px;
    max-height: 85vh;
  }
  
  @media (max-width: 360px) {
    padding: 10px;
    width: 95vw;
  }
`;

const FilterHeader = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: white;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  
  @media (max-width: 640px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: white;
  }
`;

const FilterGroup = styled.div`
  width: 100%;
  margin-bottom: 20px;
  
  @media (max-width: 640px) {
    margin-bottom: 15px;
  }
  
  @media (max-width: 360px) {
    margin-bottom: 10px;
  }
`;

const FilterGroupHeader = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 12px;
  font-weight: 500;
  
  @media (max-width: 640px) {
    font-size: 12px;
    margin-bottom: 8px;
  }
`;

const SiteTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 20px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 640px) {
    max-height: 200px;
    overflow-y: auto;
    padding-right: 5px;
    
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: #333;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #666;
      border-radius: 4px;
    }
  }
  
  @media (max-width: 360px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    max-height: 180px;
  }
`;

const SiteTypeButton = styled.button<{ isSelected: boolean }>`
  padding: 12px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isSelected ? '#2ea043' : '#333'};
  color: white;

  &:hover {
    background: ${props => props.isSelected ? '#2ea043' : '#444'};
  }
  
  @media (max-width: 640px) {
    padding: 8px 6px;
    font-size: 12px;
    border-radius: 4px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #444;
  font-size: 14px;
  outline: none;
  background: #333;
  color: white;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:hover, &:focus {
    border-color: #666;
  }
  
  @media (max-width: 640px) {
    padding: 8px;
    font-size: 12px;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #444;
  font-size: 14px;
  outline: none;
  background: #333;
  color: white;
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23FFFFFF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 12px top 50%;
  background-size: 12px auto;
  
  &:hover, &:focus {
    border-color: #666;
  }
  
  @media (max-width: 640px) {
    padding: 8px;
    font-size: 12px;
    background-position: right 8px top 50%;
  }
`;

const Option = styled.option`
  padding: 8px;
  background: #333;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'default' }>`
  width: 100%;
  padding: 12px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.variant === 'primary' ? '#3b82f6' : '#2ea043'};
  color: white;
  margin-top: 10px;

  &:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 640px) {
    padding: 10px;
    font-size: 13px;
    margin-top: 8px;
  }
`;

interface FilterControlsProps {
  selectedSiteType?: string;
  onSiteTypeChange?: (type: string) => void;
  selectedSiteTypes?: string[];
  onSiteTypesChange?: (types: string[]) => void;
  elrFilter: string;
  onElrFilterChange: (elr: string) => void;
  initialPosition: { x: number; y: number };
  onClose: () => void;
  sites?: Site[];
  equipmentFilter?: string;
  onEquipmentFilterChange?: (equipment: string) => void;
  currentAssetType?: string;
}

const SITE_TYPES = [
  'REB',
  'TEH',
  'RELAY ROOM',
  'SIGNAL BOX',
  'GATE BOX',
  'OFFICE',
  'PSB',
  'LOC',
  'DEPOT',
  'STATION',
  'EQUIPMENT ROOM',
  'HABD ROOM',
  'GSMR REPEATER',
  'TELECOMS EQUIPMENT ROOM',
  'TSC',
  'MEH',
  'Radio Site',
  'PSP',
  'SDH'
];

const FilterControls = ({
  selectedSiteType,
  onSiteTypeChange,
  selectedSiteTypes,
  onSiteTypesChange,
  elrFilter,
  onElrFilterChange,
  onClose,
  initialPosition,
  sites = [],
  equipmentFilter = '',
  onEquipmentFilterChange,
  currentAssetType = ''
}: FilterControlsProps): ReactElement<any, string | JSXElementConstructor<any>> => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [showSdhFilters, setShowSdhFilters] = useState(false);
  const [showLocFilters, setShowLocFilters] = useState(false);
  const [showPdhFilters, setShowPdhFilters] = useState(false);
  
  // Support both API styles
  const actualSelectedTypes = selectedSiteTypes || (selectedSiteType ? [selectedSiteType] : []);
  
  // Determine relevant site types based on current asset type
  const getRelevantSiteTypes = () => {
    if (currentAssetType === 'sdh_sites') {
      return ['SDH']; // Only SDH sites for SDH view
    } else if (currentAssetType === 'battery_sites') {
      return ['REB', 'TEH', 'MEH', 'PSB', 'PSP', 'SIGNAL BOX', 'RELAY ROOM', 'GATE BOX', 
              'DEPOT', 'OFFICE', 'STATION', 'EQUIPMENT ROOM', 'TELECOMS EQUIPMENT ROOM', 
              'HABD ROOM', 'GSMR REPEATER', 'TSC', 'Radio Site']; // All possible battery-related types
    } else if (currentAssetType === 'loc_sites') {
      return ['LOC']; // Only LOC sites for LOC view
    } else if (currentAssetType === 'telecoms_sites') {
      return ['TELECOMS', 'GSMR REPEATER', 'Radio Site', 'TELECOMS EQUIPMENT ROOM', 
              'TELECOMS CABINET', 'FTN SITE', 'SDH', 'PDH']; // Telecom-related types including SDH/PDH
    } else if (currentAssetType === 'pdh_sites') {
      return ['PDH']; // Only PDH sites for PDH view
    } else {
      // For regional views or all assets, show common types
      return ['REB', 'SIGNAL BOX', 'RELAY ROOM', 'LOC', 'DEPOT', 'STATION', 'SDH', 'PDH'];
    }
  };
  
  // Common site types to show by default
  const commonSiteTypes = getRelevantSiteTypes();
  const displayedSiteTypes = showAllTypes ? SITE_TYPES : commonSiteTypes;

  // Check if specific site types are present or selected
  useEffect(() => {
    // Check for SDH sites
    const hasSdhSites = sites.some(site => site.sdhInfo || site.name.siteType === 'SDH');
    const hasSdhSelected = actualSelectedTypes.includes('SDH');
    setShowSdhFilters((hasSdhSites || hasSdhSelected || currentAssetType === 'sdh_sites' || 
                       (currentAssetType === 'telecoms_sites' && sites.some(site => site.name.siteType === 'SDH'))) && 
                       currentAssetType !== 'loc_sites' && currentAssetType !== 'pdh_sites');
    
    // Check for LOC sites
    const hasLocSites = sites.some(site => site.locInfo || site.name.siteType === 'LOC');
    const hasLocSelected = actualSelectedTypes.includes('LOC');
    setShowLocFilters((hasLocSites || hasLocSelected || currentAssetType === 'loc_sites') && 
                       currentAssetType !== 'sdh_sites' && currentAssetType !== 'pdh_sites');
    
    // Check for PDH sites
    const hasPdhSites = sites.some(site => site.pdhInfo || site.name.siteType === 'PDH');
    const hasPdhSelected = actualSelectedTypes.includes('PDH');
    setShowPdhFilters((hasPdhSites || hasPdhSelected || currentAssetType === 'pdh_sites' || 
                       (currentAssetType === 'telecoms_sites' && sites.some(site => site.name.siteType === 'PDH'))) && 
                       currentAssetType !== 'sdh_sites' && currentAssetType !== 'loc_sites');
  }, [sites, actualSelectedTypes, currentAssetType]);

  // Extract unique equipment items based on site type
  const uniqueEquipmentItems = React.useMemo(() => {
    const items = new Set<string>();
    
    if (currentAssetType === 'telecoms_sites') {
      // For telecoms view, organize equipment by type
      const sdhItems = new Set<string>();
      const pdhItems = new Set<string>();
      const telecomsItems = new Set<string>();
      
      sites.forEach(site => {
        if (site.name.siteType === 'SDH' && site.sdhInfo?.item) {
          sdhItems.add(site.sdhInfo.item);
        } else if (site.name.siteType === 'PDH' && site.pdhInfo?.equipment) {
          pdhItems.add(site.pdhInfo.equipment);
        } else if (site.telecomsInfo?.item) {
          telecomsItems.add(site.telecomsInfo.item);
        }
      });
      
      // Add items in order: SDH, PDH, then others
      Array.from(sdhItems).sort().forEach(item => items.add(`SDH: ${item}`));
      Array.from(pdhItems).sort().forEach(item => items.add(`PDH: ${item}`));
      Array.from(telecomsItems).sort().forEach(item => items.add(item));
    } else {
      // Standard behavior for other views
      sites.forEach(site => {
        if (currentAssetType === 'sdh_sites' && site.sdhInfo?.item) {
          items.add(site.sdhInfo.item);
        } else if (currentAssetType === 'loc_sites' && site.locInfo?.item) {
          items.add(site.locInfo.item);
        } else if (currentAssetType === 'pdh_sites' && site.pdhInfo?.equipment) {
          items.add(site.pdhInfo.equipment);
        } else if (site.sdhInfo?.item) {
          items.add(site.sdhInfo.item);
        }
      });
    }
    
    return Array.from(items);
  }, [sites, currentAssetType]);

  // Extract unique battery models for battery sites
  const uniqueBatteryModels = React.useMemo(() => {
    const models = new Set<string>();
    
    if (currentAssetType === 'battery_sites') {
      sites.forEach(site => {
        if (site.batteryInfo?.batteryModel && site.batteryInfo.batteryModel.trim() !== '') {
          models.add(site.batteryInfo.batteryModel);
        }
      });
    }
    
    return Array.from(models).sort();
  }, [sites, currentAssetType]);

  // Extract unique cell string counts for battery sites
  const uniqueCellStringCounts = React.useMemo(() => {
    const counts = new Set<number>();
    
    if (currentAssetType === 'battery_sites') {
      sites.forEach(site => {
        if (site.batteryInfo?.noCellStrings) {
          counts.add(site.batteryInfo.noCellStrings);
        }
      });
    }
    
    return Array.from(counts).sort((a, b) => a - b);
  }, [sites, currentAssetType]);

  // Extract unique cell counts for battery sites
  const uniqueCellCounts = React.useMemo(() => {
    const counts = new Set<number>();
    
    if (currentAssetType === 'battery_sites') {
      sites.forEach(site => {
        if (site.batteryInfo?.noCells) {
          counts.add(site.batteryInfo.noCells);
        }
      });
    }
    
    return Array.from(counts).sort((a, b) => a - b);
  }, [sites, currentAssetType]);

  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  }, [onClose]);

  const handleClearFilters = () => {
    if (onSiteTypesChange) {
      onSiteTypesChange([]);
    } else if (onSiteTypeChange) {
      onSiteTypeChange('');
    }
    onElrFilterChange('');
    
    // Clear equipment filters
    if (onEquipmentFilterChange) {
      onEquipmentFilterChange('');
    }
  };

  const handleApplyFilters = () => {
    onClose();
  };
  
  const toggleShowAllTypes = () => {
    setShowAllTypes(!showAllTypes);
  };
  
  const toggleSiteType = (type: string) => {
    if (type === '') {
      // If "All Types" is selected, clear the selection
      if (onSiteTypesChange) {
        onSiteTypesChange([]);
      } else if (onSiteTypeChange) {
        onSiteTypeChange('');
      }
    } else {
      const newSelectedTypes = [...actualSelectedTypes];
      const index = newSelectedTypes.indexOf(type);
      
      if (index >= 0) {
        // Remove if already selected
        newSelectedTypes.splice(index, 1);
      } else {
        // Add if not selected
        newSelectedTypes.push(type);
      }
      
      if (onSiteTypesChange) {
        onSiteTypesChange(newSelectedTypes);
      } else if (onSiteTypeChange) {
        // For legacy API, use the first selected type or empty string if none
        onSiteTypeChange(newSelectedTypes.length > 0 ? newSelectedTypes[0] : '');
      }

      // Update SDH filters visibility
      if (type === 'SDH') {
        setShowSdhFilters(newSelectedTypes.includes('SDH'));
      }
    }
  };

  return (
    <FilterOverlay onClick={handleOutsideClick as any}>
      <FilterPanel ref={panelRef} onClick={(e) => e.stopPropagation()}>
        <FilterHeader>
          Filter {currentAssetType?.replace('_sites', '').replace('_', ' ').toUpperCase() || 'Sites'}
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </FilterHeader>
        
        {/* Only show site type filters if we have multiple types to filter by */}
        {(currentAssetType === '' || commonSiteTypes.length > 1) && (
          <FilterGroup>
            <FilterGroupHeader>FILTER BY SITE TYPE</FilterGroupHeader>
            <SiteTypeGrid>
              <SiteTypeButton 
                isSelected={actualSelectedTypes.length === 0}
                onClick={() => toggleSiteType('')}
              >
                All Types
              </SiteTypeButton>
              {displayedSiteTypes.map((type) => (
                <SiteTypeButton 
                  key={type} 
                  isSelected={actualSelectedTypes.includes(type)}
                  onClick={() => toggleSiteType(type)}
                >
                  {type}
                </SiteTypeButton>
              ))}
            </SiteTypeGrid>
            
            {!showAllTypes && SITE_TYPES.length > commonSiteTypes.length && (
              <ActionButton onClick={toggleShowAllTypes} variant="default" style={{ marginTop: '5px', padding: '8px' }}>
                Show All Types ({SITE_TYPES.length})
              </ActionButton>
            )}
            {showAllTypes && (
              <ActionButton onClick={toggleShowAllTypes} variant="default" style={{ marginTop: '5px', padding: '8px' }}>
                Show Less Types
              </ActionButton>
            )}
          </FilterGroup>
        )}
        
        <FilterGroup>
          <FilterGroupHeader>FILTER BY ELR CODE</FilterGroupHeader>
          <Input
            type="text"
            value={elrFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onElrFilterChange(e.target.value.toUpperCase())}
            placeholder="Enter ELR code (e.g. ECM1)"
          />
        </FilterGroup>

        {/* Battery-specific filters */}
        {currentAssetType === 'battery_sites' && (
          <>
            {uniqueBatteryModels.length > 0 && (
              <FilterGroup>
                <FilterGroupHeader>FILTER BY BATTERY MODEL</FilterGroupHeader>
                <Select
                  value={equipmentFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    onEquipmentFilterChange && onEquipmentFilterChange(e.target.value)
                  }
                >
                  <Option value="">All Battery Models</Option>
                  {uniqueBatteryModels.map(model => (
                    <Option key={model} value={model}>{model}</Option>
                  ))}
                </Select>
              </FilterGroup>
            )}
            
            {uniqueCellStringCounts.length > 0 && (
              <FilterGroup>
                <FilterGroupHeader>FILTER BY CELL STRINGS</FilterGroupHeader>
                <Select
                  value={equipmentFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    onEquipmentFilterChange && onEquipmentFilterChange(`strings:${e.target.value}`)
                  }
                >
                  <Option value="">All Cell Strings</Option>
                  {uniqueCellStringCounts.map(count => (
                    <Option key={`strings-${count}`} value={count.toString()}>{count} Strings</Option>
                  ))}
                </Select>
              </FilterGroup>
            )}
            
            {uniqueCellCounts.length > 0 && (
              <FilterGroup>
                <FilterGroupHeader>FILTER BY CELL COUNT</FilterGroupHeader>
                <Select
                  value={equipmentFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    onEquipmentFilterChange && onEquipmentFilterChange(`cells:${e.target.value}`)
                  }
                >
                  <Option value="">All Cell Counts</Option>
                  {uniqueCellCounts.map(count => (
                    <Option key={`cells-${count}`} value={count.toString()}>{count} Cells</Option>
                  ))}
                </Select>
              </FilterGroup>
            )}
          </>
        )}

        {/* Telecom-specific filters */}
        {currentAssetType === 'telecoms_sites' && uniqueEquipmentItems.length > 0 && (
          <FilterGroup>
            <FilterGroupHeader>FILTER BY EQUIPMENT TYPE</FilterGroupHeader>
            <Select
              value={equipmentFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                onEquipmentFilterChange && onEquipmentFilterChange(e.target.value)
              }
            >
              <Option value="">All Equipment Types</Option>
              {uniqueEquipmentItems.map(item => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </FilterGroup>
        )}

        {/* SDH-specific filters (only shown when not in telecom view) */}
        {showSdhFilters && currentAssetType !== 'telecoms_sites' && (
          <FilterGroup>
            <FilterGroupHeader>FILTER BY SDH EQUIPMENT</FilterGroupHeader>
            <Select
              value={equipmentFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                onEquipmentFilterChange && onEquipmentFilterChange(e.target.value)
              }
            >
              <Option value="">All Equipment</Option>
              {uniqueEquipmentItems.map(item => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </FilterGroup>
        )}

        {/* LOC-specific filters */}
        {showLocFilters && uniqueEquipmentItems.length > 0 && (
          <FilterGroup>
            <FilterGroupHeader>FILTER BY LOC TYPE</FilterGroupHeader>
            <Select
              value={equipmentFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                onEquipmentFilterChange && onEquipmentFilterChange(e.target.value)
              }
            >
              <Option value="">All LOC Types</Option>
              {uniqueEquipmentItems.map(item => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </FilterGroup>
        )}

        {/* PDH-specific filters (only shown when not in telecom view) */}
        {showPdhFilters && currentAssetType !== 'telecoms_sites' && uniqueEquipmentItems.length > 0 && (
          <FilterGroup>
            <FilterGroupHeader>FILTER BY PDH EQUIPMENT</FilterGroupHeader>
            <Select
              value={equipmentFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                onEquipmentFilterChange && onEquipmentFilterChange(e.target.value)
              }
            >
              <Option value="">All PDH Equipment</Option>
              {uniqueEquipmentItems.map(item => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </FilterGroup>
        )}

        <ActionButton onClick={handleClearFilters}>
          Clear Filters
        </ActionButton>
        <ActionButton variant="primary" onClick={handleApplyFilters}>
          Apply Filters
        </ActionButton>
      </FilterPanel>
    </FilterOverlay>
  );
};

export default FilterControls; 